/**
 * Package browser: a clean, multi-select list for browsing, installing, and
 * uninstalling Elyra extensions. One mental model: space toggles the change
 * you want — [x] to install a package you don't have, [-] to remove one you
 * do — and enter applies all marked changes. Windowed scrolling, installed
 * markers, and a clear call to run /reload afterwards.
 */

import type { Component } from "@elyracode/tui";
import { Container, getKeybindings, Spacer, Text, truncateToWidth, visibleWidth } from "@elyracode/tui";
import { theme } from "../theme/theme.js";
import { DynamicBorder } from "./dynamic-border.js";
import { keyHint, rawKeyHint } from "./keybinding-hints.js";

export interface PackageBrowserItem {
	/** Full package name, e.g. "@elyracode/stack-tall". */
	value: string;
	/** Short display name, e.g. "stack-tall". */
	name: string;
	/** One-line description. */
	description: string;
	/** Whether the package is already installed (shown as a ✓ marker). */
	installed?: boolean;
}

const MAX_VISIBLE = 12;
const MIN_NAME_COLUMN = 14;
const MAX_NAME_COLUMN = 28;
const NAME_GAP = 2;

/**
 * Inner scrollable, windowed list with checkbox multi-select.
 * The parent PackageBrowserComponent owns key handling and state mutation;
 * this component is purely presentational.
 */
class PackageList implements Component {
	selectedIndex = 0;
	/** Marked for install (non-installed items). */
	readonly checked = new Set<string>();
	/** Marked for uninstall (installed items). */
	readonly toRemove = new Set<string>();

	constructor(private readonly items: PackageBrowserItem[]) {}

	invalidate(): void {}

	private nameColumnWidth(): number {
		const widest = this.items.reduce((max, item) => Math.max(max, visibleWidth(item.name)), 0);
		return Math.max(MIN_NAME_COLUMN, Math.min(MAX_NAME_COLUMN, widest));
	}

	render(width: number): string[] {
		if (this.items.length === 0) {
			return [theme.fg("muted", "  No extensions available")];
		}

		const nameColumn = this.nameColumnWidth();
		const start = Math.max(
			0,
			Math.min(this.selectedIndex - Math.floor(MAX_VISIBLE / 2), this.items.length - MAX_VISIBLE),
		);
		const end = Math.min(start + MAX_VISIBLE, this.items.length);

		const lines: string[] = [];
		for (let i = start; i < end; i++) {
			const item = this.items[i];
			if (!item) continue;
			lines.push(this.renderRow(item, i === this.selectedIndex, width, nameColumn));
		}

		if (start > 0 || end < this.items.length) {
			lines.push(theme.fg("muted", `  (${this.selectedIndex + 1}/${this.items.length})`));
		}

		return lines;
	}

	private renderRow(item: PackageBrowserItem, isCursor: boolean, width: number, nameColumn: number): string {
		const isChecked = this.checked.has(item.value);
		const isMarkedRemove = this.toRemove.has(item.value);
		const cursor = isCursor ? "→ " : "  ";
		const box = isMarkedRemove ? "[-] " : isChecked ? "[x] " : "[ ] ";
		const mark = item.installed ? "✓ " : "  ";
		const name = truncateToWidth(item.name, nameColumn, "…", true);

		const used = visibleWidth(cursor) + visibleWidth(box) + visibleWidth(mark) + nameColumn + NAME_GAP;
		const descWidth = width - used - 1;
		const description = descWidth > 6 ? truncateToWidth(item.description, descWidth, "…") : "";

		const cursorPart = isCursor ? theme.fg("accent", cursor) : cursor;
		const boxPart = isMarkedRemove
			? theme.fg("error", box)
			: isChecked
				? theme.fg("success", box)
				: theme.fg("dim", box);
		const markPart = item.installed ? theme.fg("success", mark) : mark;
		const namePart = isCursor
			? theme.fg("accent", name)
			: isMarkedRemove
				? theme.fg("error", name)
				: item.installed
					? theme.fg("muted", name)
					: theme.fg("text", name);
		const descPart = description ? theme.fg("muted", `${" ".repeat(NAME_GAP)}${description}`) : "";

		return `${cursorPart}${boxPart}${markPart}${namePart}${descPart}`;
	}
}

export interface PackageBrowserActions {
	/** Package names to install. */
	install: string[];
	/** Package names to uninstall. */
	remove: string[];
}

export class PackageBrowserComponent extends Container {
	private readonly list: PackageList;
	private readonly titleText: Text;

	constructor(
		private readonly items: PackageBrowserItem[],
		private readonly onConfirm: (actions: PackageBrowserActions) => void,
		private readonly onCancel: () => void,
	) {
		super();

		this.list = new PackageList(items);

		this.addChild(new DynamicBorder());
		this.addChild(new Spacer(1));

		this.titleText = new Text("", 1, 0);
		this.addChild(this.titleText);
		this.addChild(new Spacer(1));

		this.addChild(this.list);
		this.addChild(new Spacer(1));

		this.addChild(
			new Text(
				rawKeyHint("↑↓", "navigate") +
					"  " +
					keyHint("app.packages.toggle", "mark install/remove") +
					"  " +
					keyHint("app.packages.install", "apply") +
					"  " +
					keyHint("tui.select.cancel", "cancel"),
				1,
				0,
			),
		);
		this.addChild(new Spacer(1));
		const hasInstalled = items.some((item) => item.installed);
		const legend = hasInstalled
			? `${theme.fg("success", "\u2713")}${theme.fg("dim", " = installed \u00b7 space marks ")}${theme.fg("success", "[x]")}${theme.fg("dim", " install / ")}${theme.fg("error", "[-]")}${theme.fg("dim", " remove \u00b7 changes activate after /reload.")}`
			: theme.fg("dim", "Installed extensions activate after you run /reload.");
		this.addChild(new Text(legend, 1, 0));
		this.addChild(new Spacer(1));
		this.addChild(new DynamicBorder());

		this.updateTitle();
	}

	private updateTitle(): void {
		const installCount = this.list.checked.size;
		const removeCount = this.list.toRemove.size;
		const installedCount = this.items.filter((item) => item.installed).length;
		const parts: string[] = [];
		if (installCount > 0) parts.push(`${installCount} to install`);
		if (removeCount > 0) parts.push(`${removeCount} to remove`);
		if (parts.length === 0 && installedCount > 0) parts.push(`${installedCount} installed`);
		const suffix = parts.length > 0 ? theme.fg("muted", `  (${parts.join(", ")})`) : "";
		this.titleText.setText(theme.fg("accent", theme.bold("Extensions")) + suffix);
	}

	handleInput(keyData: string): void {
		const kb = getKeybindings();

		if (kb.matches(keyData, "tui.select.up")) {
			this.list.selectedIndex = this.list.selectedIndex === 0 ? this.items.length - 1 : this.list.selectedIndex - 1;
		} else if (kb.matches(keyData, "tui.select.down")) {
			this.list.selectedIndex = this.list.selectedIndex === this.items.length - 1 ? 0 : this.list.selectedIndex + 1;
		} else if (kb.matches(keyData, "tui.select.pageUp")) {
			this.list.selectedIndex = Math.max(0, this.list.selectedIndex - MAX_VISIBLE);
		} else if (kb.matches(keyData, "tui.select.pageDown")) {
			this.list.selectedIndex = Math.min(this.items.length - 1, this.list.selectedIndex + MAX_VISIBLE);
		} else if (kb.matches(keyData, "app.packages.toggle")) {
			const item = this.items[this.list.selectedIndex];
			if (item) {
				// One mental model: space toggles the change you want. Installed
				// packages toggle a removal mark; others toggle an install mark.
				const set = item.installed ? this.list.toRemove : this.list.checked;
				if (set.has(item.value)) {
					set.delete(item.value);
				} else {
					set.add(item.value);
				}
				this.updateTitle();
			}
		} else if (kb.matches(keyData, "app.packages.install")) {
			const install = this.items.filter((item) => this.list.checked.has(item.value)).map((item) => item.value);
			const remove = this.items.filter((item) => this.list.toRemove.has(item.value)).map((item) => item.value);
			if (install.length === 0 && remove.length === 0) {
				// Nothing marked: enter installs (or reinstalls) the highlighted row.
				const current = this.items[this.list.selectedIndex];
				if (current) this.onConfirm({ install: [current.value], remove: [] });
				return;
			}
			this.onConfirm({ install, remove });
		} else if (kb.matches(keyData, "tui.select.cancel")) {
			this.onCancel();
		}
	}
}
